import React, { useEffect, useState } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { Button } from './ui/Button';
import { useLanguage } from './LanguageContext';
import { TrendingDown, PieChart } from 'lucide-react';
interface SlideMarketSizeProps {
  onContinue: () => void;
}
export function SlideMarketSize({
  onContinue
}: SlideMarketSizeProps) {
  const {
    t
  } = useLanguage();
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 500);
    const t2 = setTimeout(() => setStage(2), 1300);
    const t3 = setTimeout(() => setStage(3), 2200);
    const t4 = setTimeout(() => setStage(4), 3000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, []);
  // Donut animation — animate the stroke-dashoffset
  const targetPct = 35;
  const radius = 90;
  const circumference = 2 * Math.PI * radius;
  const pctSpring = useSpring(0, {
    stiffness: 60,
    damping: 18
  });
  const dashOffset = useTransform(pctSpring, latest => circumference - circumference * latest / 100);
  const displayPct = useTransform(pctSpring, latest => `${Math.round(latest)}%`);
  useEffect(() => {
    if (stage >= 3) pctSpring.set(targetPct);
  }, [stage]);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-583">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-4" data-id="element-584">
        <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-585">
          {t('The Scale of the Tail', 'Størrelsen af halen')}
        </h2>
        <p className="text-xl text-white/60" data-id="element-586">
          {t('Legacy revenue is the engine that funds future innovation.', 'Legacy-omsætning er motoren, der finansierer fremtidig innovation.')}
        </p>
      </motion.div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mt-10" data-id="element-587">
        {/* LEFT — the two hero stats */}
        <div className="lg:col-span-7 space-y-12" data-id="element-588">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: stage >= 2 ? 1 : 0,
          y: stage >= 2 ? 0 : 20
        }} transition={{
          duration: 0.6
        }} data-id="element-589">
            <div className="text-6xl md:text-8xl font-serif text-teal-300 tracking-tight leading-none" data-id="element-590">
              30–35<span className="text-5xl md:text-7xl" data-id="element-591">%</span>
            </div>
            <p className="text-lg md:text-xl text-white/70 mt-4 max-w-md leading-snug" data-id="element-592">
              of big pharma revenue comes from{' '}
              <span className="text-white font-medium" data-id="element-593">
                mature / legacy brands.
              </span>
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: stage >= 3 ? 1 : 0,
          y: stage >= 3 ? 0 : 20
        }} transition={{
          duration: 0.6
        }} data-id="element-594">
            <div className="text-6xl md:text-8xl font-serif text-white tracking-tight leading-none" data-id="element-595">
              $180B–300B<span className="text-teal-400" data-id="element-596">+</span>
            </div>
            <p className="text-lg md:text-xl text-white/70 mt-4 max-w-md leading-snug" data-id="element-597">
              facing{' '}
              <span className="text-white font-medium" data-id="element-598">
                loss of exclusivity
              </span>{' '}
              in the coming years.
            </p>
          </motion.div>
        </div>

        {/* RIGHT — Donut chart */}
        <div className="lg:col-span-5 flex items-center justify-center" data-id="element-599">
          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: stage >= 1 ? 1 : 0,
          scale: stage >= 1 ? 1 : 0.8
        }} transition={{
          duration: 0.8,
          type: 'spring'
        }} className="absolute inset-0 flex flex-col items-center justify-center text-center z-10" data-id="element-600">
            <span className="text-6xl font-serif text-white" data-id="element-601">30-35%</span>
            <span className="text-sm font-medium text-white/60 uppercase tracking-widest mt-2" data-id="element-602">
              {t('Of Big Pharma Revenue', 'Af Big Pharma-omsætning')}
            </span>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: stage >= 3 ? 1 : 0,
          scale: stage >= 3 ? 1 : 0.8
        }} transition={{
          duration: 0.6
        }} className="relative" data-id="element-603">
            <svg width="280" height="280" viewBox="0 0 240 240" className="overflow-visible" data-id="element-604">
              {/* Background ring */}
              <circle cx="120" cy="120" r={radius} fill="none" stroke="#1E2F4A" strokeWidth="28" data-id="element-605" />
              {/* Active ring (animated) */}
              <motion.circle cx="120" cy="120" r={radius} fill="none" stroke="#00D4AA" strokeWidth="28" strokeLinecap="round" strokeDasharray={circumference} style={{
              strokeDashoffset: dashOffset
            }} transform="rotate(-90 120 120)" data-id="element-606" />
              {/* Soft outer glow */}
              <circle cx="120" cy="120" r={radius} fill="none" stroke="#00D4AA" strokeWidth="2" strokeOpacity="0.15" data-id="element-607" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-608">
              <motion.div className="text-4xl md:text-5xl font-serif text-teal-400" data-id="element-609">
                {displayPct}
              </motion.div>
              <div className="text-white/50 text-xs uppercase tracking-widest font-medium mt-1" data-id="element-610">
                Mature / Legacy
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="space-y-8" data-id="element-611">
        <motion.div initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: stage >= 2 ? 1 : 0,
        x: stage >= 2 ? 0 : 20
      }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-8" data-id="element-612">
          <div className="flex items-start gap-4" data-id="element-613">
            <div className="w-12 h-12 rounded-full bg-teal-400/10 flex items-center justify-center flex-shrink-0" data-id="element-614">
              <TrendingDown className="w-6 h-6 text-teal-400" data-id="element-615" />
            </div>
            <div data-id="element-616">
              <h3 className="text-3xl font-serif text-white mb-2" data-id="element-617">
                $180-300B+
              </h3>
              <p className="text-white/70 leading-relaxed" data-id="element-618">
                {t('Revenue facing Loss of Exclusivity (LoE) in the coming years. This massive pool of capital is transitioning into the legacy phase.', 'Omsætning der står over for tab af eksklusivitet (LoE) i de kommende år. Denne massive pulje af kapital overgår til legacy-fasen.')}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: stage >= 3 ? 1 : 0,
        x: stage >= 3 ? 0 : 20
      }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-8" data-id="element-619">
          <div className="flex items-start gap-4" data-id="element-620">
            <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center flex-shrink-0" data-id="element-621">
              <PieChart className="w-6 h-6 text-purple-400" data-id="element-622" />
            </div>
            <div data-id="element-623">
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-624">
                {t('The Unmanaged Majority', 'Den uadministrerede majoritet')}
              </h3>
              <p className="text-white/70 leading-relaxed" data-id="element-625">
                {t('While 90% of marketing budget goes to the top 20% of products, the remaining 80% of the portfolio is left to erode naturally without support.', 'Mens 90% af markedsføringsbudgettet går til de top 20% af produkterne, overlades de resterende 80% af porteføljen til at erodere naturligt uden støtte.')}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: stage >= 4 ? 1 : 0
      }} className="pt-4" data-id="element-626">
          <Button onClick={onContinue} data-id="element-627">{t('The Problem', 'Problemet')}</Button>
        </motion.div>
      </div>

      {/* Bottom takeaway */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: stage >= 4 ? 1 : 0,
      y: stage >= 4 ? 0 : 10
    }} className="mt-8 flex items-center justify-between gap-6 bg-teal-400/5 border border-teal-400/20 rounded-xl px-6 py-4" data-id="element-628">
        <p className="text-white/80 leading-relaxed max-w-3xl" data-id="element-629">
          These brands represent a{' '}
          <span className="text-teal-400 font-semibold" data-id="element-630">
            massive, under-optimized segment
          </span>{' '}
          of pharma portfolios — the part that quietly funds the next generation
          of innovation.
        </p>
        <Button onClick={onContinue} data-id="element-631">The Problem</Button>
      </motion.div>
    </div>;
}